import { useIonRouter } from '@ionic/react';
import type { Workspace } from '@up2stay/up2promo-api-client';
import { Avatar, AvatarFallback, AvatarImage } from '@up2stay/up2promo-ui';
import { Button } from '@up2stay/up2promo-ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@up2stay/up2promo-ui';
import { ChevronsUpDown, Plus } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';

import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useWorkspaceBootstrapStatus } from '@/core/hooks/use-workspace-bootstrap';
import { getAbsoluteFileUrl } from '@/core/lib/file-url';
import { workspaceCreatePath } from '@/core/lib/workspace-create-return';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import {
  useCurrentWorkspace,
  useSetWorkspace,
  useWorkspaceStore,
} from '@/core/store/workspace.store';

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from './sidebar';
import { switcherAvatarFallbackClass } from './switcher-avatar-styles';

type WorkspaceSwitcherProps = {
  workspaces: Workspace[];
  isLoading?: boolean;
  /** @deprecated Prefer bootstrap status; kept for callers that still pass it. */
  isFetched?: boolean;
  isError?: boolean;
};

function SwitcherSkeleton({ label }: { label: string }) {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton size="lg" disabled className="opacity-70">
          <Avatar className="size-8 rounded-lg">
            <AvatarFallback
              className={`rounded-lg text-xs font-semibold ${switcherAvatarFallbackClass}`}
            >
              —
            </AvatarFallback>
          </Avatar>
          <div className="grid flex-1 text-start text-sm leading-tight">
            <span className="truncate font-semibold text-muted-foreground">
              {label}
            </span>
          </div>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

export function WorkspaceSwitcher({
  workspaces,
  isLoading = false,
  isError = false,
}: WorkspaceSwitcherProps) {
  const dict = useDictionary();
  const { isMobile } = useSidebar();
  const ionRouter = useIonRouter();
  const { navigateShell } = useNavigateShell();
  const workspace = useCurrentWorkspace();
  const setWorkspace = useSetWorkspace();
  const bootstrapStatus = useWorkspaceBootstrapStatus();

  const hasInitializedRef = useRef(false);
  const hydrated = useWorkspaceStore((state) => state.hydrated);

  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (!hydrated) return;
    hasInitializedRef.current = true;
  }, [workspaces, workspace, isLoading, setWorkspace, hydrated]);

  const currentSelection = useMemo(() => {
    const ws = workspace ?? workspaces[0];
    if (!ws) {
      return {
        name: dict.workspace.workspace,
        subtitle: dict.workspace.workspace,
        avatar: undefined as string | undefined,
        initials: '—',
      };
    }
    return {
      name: ws.name || dict.workspace.workspace,
      subtitle: dict.workspace.workspace,
      avatar: ws.photo?.path ? getAbsoluteFileUrl(ws.photo.path) : undefined,
      initials: (ws.name || dict.workspace.workspace)
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2),
    };
  }, [workspace, workspaces, dict]);

  const handleSelectWorkspace = (ws: Workspace) => {
    setWorkspace(ws);
  };

  const handleCreateWorkspace = () => {
    // Zero workspaces → mandatory /onboarding; otherwise in-shell create page.
    if (workspaces.length === 0) {
      ionRouter.push('/onboarding', 'root', 'replace');
      return;
    }
    navigateShell(workspaceCreatePath(window.location.pathname));
  };

  if (isError || bootstrapStatus === 'error') {
    return null;
  }

  // Skeleton until bootstrap is ready. Create-first only when memberships are
  // confirmed empty for this session (`needs-onboarding` + length 0). Plan-step
  // resume also uses `needs-onboarding` with workspaces present — never flash
  // create-first there (DEV-470).
  if (bootstrapStatus !== 'ready') {
    if (bootstrapStatus === 'needs-onboarding' && workspaces.length === 0) {
      return (
        <SidebarMenu>
          <SidebarMenuItem>
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={handleCreateWorkspace}
            >
              <Plus className="h-4 w-4" />
              {dict.sidebar?.createFirstWorkspace ??
                'Create your first workspace'}
            </Button>
          </SidebarMenuItem>
        </SidebarMenu>
      );
    }
    return <SwitcherSkeleton label={dict.workspace.workspace} />;
  }

  if (workspaces.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <Button
            variant="outline"
            className="w-full justify-start gap-2"
            onClick={handleCreateWorkspace}
          >
            <Plus className="h-4 w-4" />
            {dict.sidebar?.createFirstWorkspace ??
              'Create your first workspace'}
          </Button>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="cursor-pointer data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              disabled={isLoading}
            >
              <Avatar className="size-8 rounded-lg">
                {currentSelection.avatar ? (
                  <AvatarImage
                    src={currentSelection.avatar}
                    alt={currentSelection.name}
                  />
                ) : null}
                <AvatarFallback
                  className={`rounded-lg text-xs font-semibold ${switcherAvatarFallbackClass}`}
                >
                  {currentSelection.initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-semibold">
                  {currentSelection.name}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {currentSelection.subtitle}
                </span>
              </div>
              <ChevronsUpDown className="ms-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            align="start"
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {dict.workspace.workspaces}
            </DropdownMenuLabel>

            {workspaces.map((ws) => (
              <DropdownMenuItem
                key={ws.id}
                onClick={() => handleSelectWorkspace(ws)}
                className={`cursor-pointer gap-2 p-2 ${
                  workspace?.id === ws.id ? 'bg-sidebar-accent' : ''
                }`}
              >
                <div className="flex size-6 items-center justify-center rounded-sm border overflow-hidden">
                  <Avatar
                    key={`ws-dropdown-${ws.id}-${ws.photo?.path || 'no-photo'}`}
                    className="size-6 rounded-sm"
                  >
                    {ws.photo?.path && ws.photo.path.trim() ? (
                      <AvatarImage
                        src={getAbsoluteFileUrl(ws.photo.path)}
                        alt={ws.name || 'Workspace'}
                      />
                    ) : null}
                    <AvatarFallback
                      className={`rounded-sm text-xs font-semibold ${switcherAvatarFallbackClass}`}
                    >
                      {(ws.name || dict.workspace.workspace)
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex flex-col">
                  <span>{ws.name || dict.workspace.workspace}</span>
                  <span className="text-xs text-muted-foreground">
                    {dict.workspace.workspace}
                  </span>
                </div>
                {workspace?.id === ws.id && (
                  <span className="ms-auto text-xs text-muted-foreground">
                    ✓
                  </span>
                )}
              </DropdownMenuItem>
            ))}

            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer gap-2 p-2"
              onClick={handleCreateWorkspace}
            >
              <div className="bg-background flex size-6 items-center justify-center rounded-md border">
                <Plus className="size-4" />
              </div>
              <div className="text-muted-foreground font-medium">
                {dict.workspace.createWorkspace}
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
